import { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, ListChecks, Plus, RotateCcw } from 'lucide-react';
import {
  type WarehouseCountSessionRecord,
  type WarehouseGateway,
  type WarehouseLocationRecord,
  type WarehouseStockRecord,
} from '@forq/warehouse-data';

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Die Aktion ist fehlgeschlagen.';
}
function qty(value: number): string {
  return value.toLocaleString('de-DE', { maximumFractionDigits: 3 });
}
const statusLabels: Record<WarehouseCountSessionRecord['status'], string> = {
  counting: 'Zählung läuft', posted: 'Gebucht', cancelled: 'Abgebrochen',
};

export function InventurSection({ tenantId, gateway, locations, canPost, onPosted }: {
  readonly tenantId: string;
  readonly gateway: WarehouseGateway;
  readonly locations: readonly WarehouseLocationRecord[];
  readonly canPost: boolean;
  /** Nach dem Abschluss aufrufen, damit die übrigen Lager-Reiter (Bestände/Bewegungen) frisch laden. */
  readonly onPosted?: () => void;
}) {
  const [sessions, setSessions] = useState<readonly WarehouseCountSessionRecord[]>([]);
  const [active, setActive] = useState<WarehouseCountSessionRecord | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState<'inventur' | 'frei'>('inventur');

  const loadSessions = useCallback(async () => {
    try { setSessions(await gateway.listCountSessions(tenantId)); }
    catch (loadError) { setError(messageOf(loadError)); }
  }, [gateway, tenantId]);

  useEffect(() => { void loadSessions(); }, [loadSessions]);

  const openActive = useCallback((session: WarehouseCountSessionRecord) => {
    setActive(session);
    setDrafts(Object.fromEntries(session.lines.map((line) => [line.id, line.countedQuantity == null ? '' : String(line.countedQuantity)])));
  }, []);

  const selectSession = useCallback(async (sessionId: string) => {
    setBusy(true); setError(null);
    try { openActive(await gateway.getCountSession(tenantId, sessionId)); }
    catch (selectError) { setError(messageOf(selectError)); }
    finally { setBusy(false); }
  }, [gateway, tenantId, openActive]);

  const startCount = useCallback(async (locationId: string | null, note: string) => {
    setBusy(true); setError(null);
    try {
      const session = await gateway.openCountSession({ tenantId, locationId, note });
      setDialogOpen(false);
      openActive(session);
      await loadSessions();
    } catch (startError) { setError(messageOf(startError)); }
    finally { setBusy(false); }
  }, [gateway, tenantId, openActive, loadSessions]);

  const commitLine = useCallback(async (lineId: string, raw: string) => {
    if (!active) return;
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? null : Number(trimmed.replace(',', '.'));
    if (parsed != null && (!Number.isFinite(parsed) || parsed < 0)) {
      setError('Die gezählte Menge muss 0 oder größer sein.');
      return;
    }
    setError(null);
    try {
      const line = await gateway.saveCountLine({ tenantId, sessionId: active.id, lineId, countedQuantity: parsed });
      setActive((current) => current ? { ...current, lines: current.lines.map((existing) => existing.id === lineId ? line : existing) } : current);
    } catch (saveError) { setError(messageOf(saveError)); }
  }, [active, gateway, tenantId]);

  const postCount = useCallback(async () => {
    if (!active) return;
    if (!window.confirm('Inventur abschließen? Alle Differenzen werden als Bestandskorrekturen gebucht.')) return;
    setBusy(true); setError(null);
    try {
      openActive(await gateway.postCountSession({ tenantId, sessionId: active.id }));
      await loadSessions();
      onPosted?.();
    } catch (postError) { setError(messageOf(postError)); }
    finally { setBusy(false); }
  }, [active, gateway, tenantId, openActive, loadSessions, onPosted]);

  if (mode === 'frei') {
    return <FreeCountSheet tenantId={tenantId} gateway={gateway} locations={locations} canPost={canPost} onBack={() => setMode('inventur')} onPosted={onPosted} />;
  }

  if (active) {
    const counted = active.lines.filter((line) => line.countedQuantity != null).length;
    const differences = active.lines.filter((line) => line.countedQuantity != null && line.difference !== 0).length;
    return (
      <section className="warehouse-inventur">
        <header className="warehouse-inventur__head">
          <button type="button" className="warehouse-inventur__back" onClick={() => setActive(null)}><RotateCcw size={14} /> Übersicht</button>
          <div>
            <span className="warehouse-eyebrow">Inventur {active.reference}</span>
            <p>{statusLabels[active.status]} · {active.locationCode || 'ganzes Lager'} · {counted}/{active.totalLines} gezählt · {differences} Differenz{differences === 1 ? '' : 'en'}</p>
          </div>
          {active.status === 'counting' && canPost ? (
            <button type="button" className="warehouse-inventur__post" disabled={busy} onClick={() => void postCount()}><ClipboardCheck size={15} /> Inventur abschließen</button>
          ) : null}
        </header>
        {error ? <div className="warehouse-inventur__error">{error}</div> : null}
        <div className="warehouse-inventur__grid">
          <div className="warehouse-inventur__grid-head"><span>Artikel</span><span>Lagerplatz</span><span>Buchbestand</span><span>Gezählt</span><span>Differenz</span></div>
          {active.lines.map((line) => {
            const showDiff = line.countedQuantity != null;
            return (
              <div className="warehouse-inventur__row" key={line.id}>
                <span className="forq-stack"><strong>{line.itemName}</strong><small>{line.itemNumber}</small></span>
                <span>{line.locationCode}</span>
                <span className="warehouse-inventur__num">{qty(line.bookQuantity)} {line.unit}</span>
                <span>
                  {active.status === 'counting' ? (
                    <input type="number" min="0" step="0.001" inputMode="decimal" value={drafts[line.id] ?? ''}
                      onChange={(event) => setDrafts((current) => ({ ...current, [line.id]: event.target.value }))}
                      onBlur={(event) => void commitLine(line.id, event.target.value)} />
                  ) : <span className="warehouse-inventur__num">{line.countedQuantity == null ? '–' : qty(line.countedQuantity)}</span>}
                </span>
                <span className={`warehouse-inventur__num${showDiff && line.difference > 0 ? ' is-plus' : ''}${showDiff && line.difference < 0 ? ' is-minus' : ''}`}>
                  {showDiff ? `${line.difference > 0 ? '+' : ''}${qty(line.difference)}` : ''}
                </span>
              </div>
            );
          })}
          {active.lines.length === 0 ? <p className="warehouse-inventur__empty">Im Zählbereich gibt es keine bestandsgeführten Artikel.</p> : null}
        </div>
      </section>
    );
  }

  return (
    <section className="warehouse-inventur">
      <header className="warehouse-inventur__head">
        <div>
          <span className="warehouse-eyebrow">Inventur</span>
          <p>Stichtagsinventur: Zählliste aus dem Buchbestand erzeugen, gezählte Mengen erfassen, Differenzen als Korrektur buchen.</p>
        </div>
        <div className="warehouse-inventur__head-actions">
          <button type="button" className="warehouse-secondary" onClick={() => setMode('frei')}><ListChecks size={15} /> Freie Zählung</button>
          {canPost ? <button type="button" className="warehouse-inventur__post" onClick={() => setDialogOpen(true)}><Plus size={15} /> Neue Inventur</button> : null}
        </div>
      </header>
      {error && !dialogOpen ? <div className="warehouse-inventur__error">{error}</div> : null}
      {sessions.length === 0 ? (
        <div className="warehouse-inventur__empty">Noch keine Inventur erfasst.{canPost ? ' Starte oben eine neue Zählung.' : ''}</div>
      ) : (
        <ul className="warehouse-inventur__list">
          {sessions.map((session) => (
            <li key={session.id}>
              <button type="button" onClick={() => void selectSession(session.id)}>
                <span className="forq-stack"><strong>{session.reference}</strong><small>{new Date(session.createdAt).toLocaleDateString('de-DE')} · {session.locationCode || 'ganzes Lager'}</small></span>
                <span className={`warehouse-inventur__pill is-${session.status}`}>{statusLabels[session.status]}</span>
                <span>{session.countedLines}/{session.totalLines} gezählt</span>
                <span>{session.differenceLines} Differenz{session.differenceLines === 1 ? '' : 'en'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {dialogOpen ? <NewCountDialog locations={locations} busy={busy} error={error} onCancel={() => setDialogOpen(false)} onStart={startCount} /> : null}
    </section>
  );
}

function NewCountDialog({ locations, busy, error, onCancel, onStart }: {
  readonly locations: readonly WarehouseLocationRecord[];
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onStart: (locationId: string | null, note: string) => void;
}) {
  const [scope, setScope] = useState<string>('all');
  const [note, setNote] = useState('');
  return (
    <div className="warehouse-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
      <div className="warehouse-modal__panel is-small">
        <span className="warehouse-eyebrow">Inventur</span><h2>Neue Zählung starten</h2>
        <div className="warehouse-form-grid">
          <label className="is-wide">Zählbereich
            <select value={scope} onChange={(event) => setScope(event.target.value)}>
              <option value="all">Ganzes Lager</option>
              {locations.filter((location) => location.active).map((location) => <option key={location.id} value={location.id}>{location.code} · {location.name}</option>)}
            </select>
          </label>
          <label className="is-wide">Notiz (optional)<input value={note} onChange={(event) => setNote(event.target.value)} placeholder="z. B. Jahresinventur 2026" /></label>
        </div>
        {error ? <div className="warehouse-modal__error" role="alert">{error}</div> : null}
        <div className="warehouse-modal__actions">
          <button type="button" className="warehouse-secondary" onClick={onCancel}>Abbrechen</button>
          <button type="button" className="warehouse-primary" disabled={busy} onClick={() => onStart(scope === 'all' ? null : scope, note)}>Zählliste erzeugen</button>
        </div>
      </div>
    </div>
  );
}

function stockRowKey(row: WarehouseStockRecord): string {
  return `${row.itemId} ${row.locationId}`;
}
function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * Freie Zählung: Bestand zwischendurch zählen, OHNE eine formale Inventur (kein persistierter
 * Zählbeleg). Baut die Zählliste live aus dem aktuellen Buchbestand (searchStock), Differenzen
 * werden einzeln oder gesammelt als ganz normale Bestandskorrektur (adjustment-Bewegung) gebucht --
 * genau das, was die Inventur je Position auch tut, nur ad hoc und ohne Sitzung.
 */
function FreeCountSheet({ tenantId, gateway, locations, canPost, onBack, onPosted }: {
  readonly tenantId: string;
  readonly gateway: WarehouseGateway;
  readonly locations: readonly WarehouseLocationRecord[];
  readonly canPost: boolean;
  readonly onBack: () => void;
  readonly onPosted?: () => void;
}) {
  const [locationId, setLocationId] = useState<string | 'all'>('all');
  const [rows, setRows] = useState<readonly WarehouseStockRecord[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const page = await gateway.searchStock({ tenantId, locationId, pageSize: 500 });
      setRows(page.items);
    } catch (loadError) { setError(messageOf(loadError)); }
    finally { setLoading(false); }
  }, [gateway, tenantId, locationId]);
  useEffect(() => { void load(); }, [load]);

  const countedOf = useCallback((row: WarehouseStockRecord): number | null => {
    const raw = drafts[stockRowKey(row)];
    if (raw == null || raw.trim() === '') return null;
    const parsed = Number(raw.replace(',', '.'));
    return Number.isFinite(parsed) && parsed >= 0 ? round3(parsed) : null;
  }, [drafts]);

  const deltaOf = useCallback((row: WarehouseStockRecord): number | null => {
    const counted = countedOf(row);
    return counted == null ? null : round3(counted - row.onHand);
  }, [countedOf]);

  const bookRows = useCallback(async (targets: readonly WarehouseStockRecord[]) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      for (const row of targets) {
        const counted = countedOf(row);
        const delta = deltaOf(row);
        if (counted == null || delta == null || delta === 0) continue;
        await gateway.postMovement({
          tenantId, type: 'adjustment', itemId: row.itemId, targetLocationId: row.locationId,
          quantity: delta, reason: 'Freie Zählung: Bestandskorrektur',
        });
      }
      setDrafts((current) => {
        const next = { ...current };
        for (const row of targets) delete next[stockRowKey(row)];
        return next;
      });
      await load();
      onPosted?.();
      setNotice(targets.length === 1
        ? `${targets[0].itemName}: Bestand korrigiert.`
        : `${targets.length} Korrekturen gebucht.`);
    } catch (bookError) { setError(messageOf(bookError)); }
    finally { setBusy(false); }
  }, [gateway, tenantId, countedOf, deltaOf, load, onPosted]);

  const pending = rows.filter((row) => { const delta = deltaOf(row); return delta != null && delta !== 0; });

  const bookAll = useCallback(() => {
    if (pending.length === 0) return;
    if (!window.confirm(`${pending.length} Differenz${pending.length === 1 ? '' : 'en'} als Bestandskorrektur buchen?`)) return;
    void bookRows(pending);
  }, [pending, bookRows]);

  return (
    <section className="warehouse-inventur">
      <header className="warehouse-inventur__head">
        <button type="button" className="warehouse-inventur__back" onClick={onBack}><RotateCcw size={14} /> Inventur</button>
        <div>
          <span className="warehouse-eyebrow">Freie Zählung</span>
          <p>Bestand zwischendurch zählen – ohne formale Inventur. Gezählte Mengen eintragen; Differenzen einzeln oder gesammelt als Korrektur buchen.</p>
        </div>
        <select className="warehouse-freecount__scope" value={locationId} onChange={(event) => setLocationId(event.target.value)}>
          <option value="all">Alle Lagerplätze</option>
          {locations.filter((location) => location.active).map((location) => <option key={location.id} value={location.id}>{location.code} · {location.name}</option>)}
        </select>
      </header>
      {error ? <div className="warehouse-inventur__error">{error}</div> : null}
      {notice ? <div className="warehouse-inventur__notice">{notice}</div> : null}
      <div className="warehouse-inventur__grid warehouse-freecount__grid">
        <div className="warehouse-inventur__grid-head"><span>Artikel</span><span>Lagerplatz</span><span>Buchbestand</span><span>Gezählt</span><span>Differenz</span><span></span></div>
        {loading ? <p className="warehouse-inventur__empty">Bestand wird geladen …</p> : null}
        {!loading && rows.length === 0 ? <p className="warehouse-inventur__empty">Kein bestandsgeführter Artikel in diesem Bereich.</p> : null}
        {rows.map((row) => {
          const delta = deltaOf(row);
          return (
            <div className="warehouse-inventur__row warehouse-freecount__row" key={stockRowKey(row)}>
              <span className="forq-stack"><strong>{row.itemName}</strong><small>{row.itemNumber}</small></span>
              <span>{row.locationCode}</span>
              <span className="warehouse-inventur__num">{qty(row.onHand)} {row.unit}</span>
              <span>
                <input type="number" min="0" step="0.001" inputMode="decimal" value={drafts[stockRowKey(row)] ?? ''}
                  onChange={(event) => setDrafts((current) => ({ ...current, [stockRowKey(row)]: event.target.value }))} />
              </span>
              <span className={`warehouse-inventur__num${delta != null && delta > 0 ? ' is-plus' : ''}${delta != null && delta < 0 ? ' is-minus' : ''}`}>
                {delta == null ? '' : `${delta > 0 ? '+' : ''}${qty(delta)}`}
              </span>
              <span>{canPost && delta != null && delta !== 0 ? <button type="button" className="warehouse-secondary" disabled={busy} onClick={() => void bookRows([row])}>Korrigieren</button> : null}</span>
            </div>
          );
        })}
      </div>
      {canPost && pending.length > 0 ? (
        <div className="warehouse-freecount__foot">
          <span>{pending.length} offene Differenz{pending.length === 1 ? '' : 'en'}</span>
          <button type="button" className="warehouse-inventur__post" disabled={busy} onClick={bookAll}><ClipboardCheck size={15} /> Alle Differenzen buchen</button>
        </div>
      ) : null}
    </section>
  );
}
